/* eslint-disable no-mixed-spaces-and-tabs */
/* eslint-disable react/jsx-wrap-multilines */
import React, { useCallback, useEffect } from 'react';
import { createStructuredSelector } from 'reselect';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { useHistory, useLocation } from 'react-router';
import { NAV_PAGE_LIST, TABLE_COLUMNS } from './constants';
import { useInjectSaga } from '../../utils/injectSaga';
import saga from './saga';
import { useInjectReducer } from '../../utils/injectReducer';
import reducer from './reducer';
import { getThermoboxesSelector, getIsThermoboxesLoadingSelector, getPaginationSelector } from './selectors';
import { getThermoboxesAction, resetStateAction } from './actions';
import { getIsAvailableThermoboxesButtonsSelector } from '../profile/selectors';
import { removeModalQuery } from '../../utils/removeModalQuery';
import { DataGridComponent } from '../../components/data-grid';
import { normalizeDataGridModel } from '../../utils/normalizeDataGridModel';
import { PageContentWithNav } from '../../components/page-content-with-nav';

const ThermoboxesContainerWrapper = ({
	onGetThermoboxes,
	thermoboxes,
	isThermoboxesLoading,
	pagination,
	onResetState,
	isAvailable,
}) => {
	useInjectSaga({ key: 'thermoboxesContainerSaga', saga });
	useInjectReducer({ key: 'thermoboxesContainerReducer', reducer });

	const { search, pathname } = useLocation();

	const query = new URLSearchParams(search);

	const history = useHistory();

	useEffect(() => {
		onGetThermoboxes({ searchQuery: removeModalQuery(query.toString()) });

		return () => onResetState();
	}, []);

	const handleAddButtonClick = () => {
		query.set('modal', 'thermobox');
		query.set('mode', 'add');

		history.push(`${pathname}?${query}`);
	};

	const handlePageChange = (_, { activePage }) => {
		query.set('page', activePage);

		history.push(`${pathname}?${query}`);

		onGetThermoboxes({ searchQuery: removeModalQuery(query.toString()) });
	};

	const handleSelectChange = (_, { value }) => {
		query.set('page', 1);

		if (value === 'all') {
			query.delete('per_page');
		} else {
			query.set('per_page', value);
		}

		history.push(`${pathname}?${query}`);

		onGetThermoboxes({ searchQuery: removeModalQuery(query.toString()) });
	};

	const handleSortChange = useCallback(sortModel => {
		const jsonSorts = JSON.stringify(sortModel);

		query.set('sorts', jsonSorts);

		history.push(`${pathname}?${query}`);

		onGetThermoboxes({ searchQuery: removeModalQuery(query.toString()) });
	});

	const handleFilterChange = useCallback((filterModel, action) => {
		if (action.reason) {
			const jsonFilters = JSON.stringify(normalizeDataGridModel(filterModel, TABLE_COLUMNS));

			query.delete('page');
			query.set('filters', jsonFilters);

			history.push(`${pathname}?${query}`);

			onGetThermoboxes({ searchQuery: removeModalQuery(query.toString()) });
		}
	});

	return (
		<PageContentWithNav
			pageNavigationItems={NAV_PAGE_LIST}
			isInnerRouter
			pageContent={
				<DataGridComponent
					headerText="Термоконтейнеры"
					columns={TABLE_COLUMNS}
					onAddButtonClick={isAvailable.create ? handleAddButtonClick : null}
					rows={thermoboxes}
					pagination={
						!pagination
							? null
							: {
									...pagination,
									onPageChange: handlePageChange,
									onSelectChange: handleSelectChange,
							  }
					}
					isTableDataLoading={isThermoboxesLoading}
					handleSortChange={handleSortChange}
					handleFilterChange={handleFilterChange}
				/>
			}
		/>
	);
};

const mapStateToProps = createStructuredSelector({
	thermoboxes: getThermoboxesSelector(),
	isThermoboxesLoading: getIsThermoboxesLoadingSelector(),
	pagination: getPaginationSelector(),
	isAvailable: getIsAvailableThermoboxesButtonsSelector(),
});

const mapDispatchToProps = {
	onGetThermoboxes: getThermoboxesAction,
	onResetState: resetStateAction,
};

const ThermoboxesContainer = connect(mapStateToProps, mapDispatchToProps)(ThermoboxesContainerWrapper);

ThermoboxesContainerWrapper.propTypes = {
	onGetThermoboxes: PropTypes.func.isRequired,
	thermoboxes: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.number.isRequired,
			name: PropTypes.string,
			link: PropTypes.string.isRequired,
		}),
	),
	isThermoboxesLoading: PropTypes.bool.isRequired,
	pagination: PropTypes.shape({
		currentPage: PropTypes.number,
		totalPages: PropTypes.number.isRequired,
		total: PropTypes.number,
		from: PropTypes.number,
		to: PropTypes.number,
		isNextItem: PropTypes.bool,
		isPrevItem: PropTypes.bool,
		isFirstItem: PropTypes.bool,
		isLastItem: PropTypes.bool,
	}),
	onResetState: PropTypes.func.isRequired,
	isAvailable: PropTypes.objectOf(PropTypes.bool),
};

export default ThermoboxesContainer;
